import React from "react";
import image from "../../img/Renders_Wizard/render12.png";
import { useTranslation } from "react-i18next";
import { Eye, Mic, Shield, Video, Zap } from "lucide-react";

export const Camera = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: <Video className="w-5 h-5" />,
      text: t("product.camera_feature_2"),
    },
    {
      icon: <Eye className="w-5 h-5" />,
      text: t("product.camera_feature_3"),
    },
    {
      icon: <Mic className="w-5 h-5" />,
      text: t("product.camera_feature_4"),
    },
    {
      icon: <Shield className="w-5 h-5" />,
      text: t("product.camera_feature_5"),
    },
    {
      icon: <Zap className="w-5 h-5" />,
      text: t("product.camera_feature_6"),
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50 py-16 lg:py-24">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-200 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content Side */}
          <div className="space-y-8">
            {/* Header with icon */}
            <div className="space-y-4">
              {/* <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-100 rounded-full text-blue-700 font-medium text-sm">
                <Camera className="w-4 h-4" />
                Professional Equipment
              </div> */}

              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent leading-tight">
                {t("product.4k_panoramic_camera")}
              </h2>

              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                {t("product.camera_feature_1")}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group flex items-start gap-4 p-4 rounded-xl bg-white/70 backdrop-blur-sm border border-gray-100 hover:border-blue-200 hover:bg-white/90 transition-all duration-300 hover:shadow-lg hover:shadow-blue-100/50"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <p className="text-gray-700 font-medium leading-relaxed pt-1">
                    {feature.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
              <p className="text-lg font-medium text-gray-800 leading-relaxed">
                {t("product.camera_summary")}
              </p>
            </div>

            {/* CTA Button */}
            {/* <div className="flex gap-4">
              <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-200/50 transform hover:-translate-y-1">
                {t("product.learn_more")}
              </button>
              <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-blue-500 hover:text-blue-600 transition-all duration-300 hover:bg-blue-50">
                {t("product.view_specs")}
              </button>
            </div> */}
          </div>

          {/* Image Side */}
          <div className="relative">
            {/* Floating elements */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl opacity-20 blur-xl animate-pulse"></div>
            <div
              className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full opacity-20 blur-xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>

            {/* Main image container */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>

              <div className="relative bg-white rounded-3xl p-8 shadow-2xl shadow-blue-100/50 group-hover:shadow-3xl group-hover:shadow-blue-200/60 transition-all duration-500 transform group-hover:-translate-y-2">
                {/* Placeholder for actual image */}
                <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center overflow-hidden">
                  <div className="text-center space-y-4">
                    <img src={image} alt="wizard-device" />
                    {/* <Camera className="w-16 h-16 text-gray-400 mx-auto" /> */}
                    <p className="text-gray-500 font-medium">
                      {t("product.4k_panoramic_camera")}
                    </p>
                    <div className="flex justify-center space-x-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <div
                        className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"
                        style={{ animationDelay: "0.4s" }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Tech specs overlay */}
                <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-medium">
                  4K • 60fps
                </div>

                {/* Feature badges */}
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <span className="px-3 py-1 bg-blue-500/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                    180° View
                  </span>
                  <span className="px-3 py-1 bg-purple-500/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                    {t("product.ai_tracking")}
                  </span>
                </div>
              </div>
            </div>

            {/* Stats cards */}
            <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 hidden lg:block">
              <div className="bg-white rounded-2xl p-4 shadow-xl shadow-blue-100/50 border border-gray-100">
                <div className="text-2xl font-bold text-blue-600">4K</div>
                <div className="text-sm text-gray-600">
                  {t("product.resolution")}
                </div>
              </div>
            </div>

            <div className="absolute -right-8 top-1/4 hidden lg:block">
              <div className="bg-white rounded-2xl p-4 shadow-xl shadow-blue-100/50 border border-gray-100">
                <div className="text-2xl font-bold text-purple-600">180°</div>
                <div className="text-sm text-gray-600">
                  {t("product.field_of_view")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
